import { AnalyticsEventName } from '@dupe/common/constants'
import clsx from 'clsx'
import { memo, useEffect } from 'react'
import AnimatedLabelDot from '~/src/components/AnimatedLabelDot'
import LoadingCanvasDots from '~/src/components/LoadingCanvasDots'
import { useAuthContext } from '~/src/context/AuthProvider'
import { useCanvasContext } from '~/src/context/CanvasProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import logger from '~/src/lib/logger'

interface Props {
  details: {
    shopKey: string
    hash: string | null | undefined
    source: string
  }
  children: (props: {
    canvasWidth: number
    canvasHeight: number
  }) => React.ReactNode
}

// Memoized rect component
const GeneratedRect = memo(function GeneratedRect({
  rect,
  index,
  selectedIndex,
  hoveredIndex,
  setHoveredIndex,
  loadingIndex,
  onClick,
}: {
  rect: any
  index: number
  selectedIndex: number | undefined
  hoveredIndex: number | undefined
  setHoveredIndex: (index: number | undefined) => void
  loadingIndex: number | undefined
  onClick: () => void
}) {
  const isLoading = loadingIndex === index
  return (
    <div
      className={clsx(
        'generated-rect group absolute flex cursor-pointer items-center justify-center',
        {
          'rounded-lg': selectedIndex === index,
        }
      )}
      onMouseEnter={() => setHoveredIndex(index)}
      onMouseLeave={() => setHoveredIndex(undefined)}
      style={{
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        zIndex: 10,
      }}
      onClick={onClick}
    >
      <div
        className={clsx(
          'absolute -left-1 -top-1 h-5 w-5 scale-0 rounded-tl-[20px] border-l-4 border-t-4 border-white opacity-0 transition-opacity duration-300 group-hover:scale-100  group-hover:opacity-100',
          {
            'scale-100 opacity-100':
              selectedIndex === index || hoveredIndex === index,
          }
        )}
      />
      <div
        className={clsx(
          'absolute -right-1 -top-1 h-5 w-5 scale-0 rounded-tr-[20px] border-r-4 border-t-4 border-white opacity-0 transition-opacity duration-300 group-hover:scale-100  group-hover:opacity-100',
          {
            'scale-100 opacity-100':
              selectedIndex === index || hoveredIndex === index,
          }
        )}
      />
      <div
        className={clsx(
          'absolute -bottom-1 -left-1 h-5 w-5 scale-0 rounded-bl-[20px] border-b-4 border-l-4 border-white opacity-0 transition-opacity duration-300 group-hover:scale-100  group-hover:opacity-100',
          {
            'scale-100 opacity-100':
              selectedIndex === index || hoveredIndex === index,
          }
        )}
      />
      <div
        className={clsx(
          'absolute -bottom-1 -right-1 h-5 w-5 scale-0 rounded-br-[20px] border-b-4 border-r-4 border-white opacity-0 transition-opacity duration-300 group-hover:scale-100  group-hover:opacity-100',
          {
            'scale-100 opacity-100':
              selectedIndex === index || hoveredIndex === index,
          }
        )}
      />
      <AnimatedLabelDot
        key={index}
        title={isLoading ? 'Loading' : rect.title}
        focus={selectedIndex === index || hoveredIndex === index || isLoading}
      />
    </div>
  )
})

// Memoized clicked rect component
const ClickedRect = memo(function ClickedRect({
  rect,
  hoveredIndex,
  selectedIndex,
  loadingIndex,
}: {
  rect: any
  hoveredIndex: number | undefined
  selectedIndex: number | undefined
  loadingIndex: number | undefined
}) {
  const isSelected =
    (typeof hoveredIndex === 'undefined' && selectedIndex === -1) ||
    typeof selectedIndex === 'undefined'

  return (
    <div
      className={clsx(
        'user-generated-rect group pointer-events-none absolute flex cursor-pointer items-center justify-center',
        {
          'rounded-lg': isSelected,
        }
      )}
      style={{
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        zIndex: 10,
      }}
    >
      <div
        className={clsx(
          'absolute -left-1 -top-1 h-5 w-5 scale-0 rounded-tl-[20px] border-l-4 border-t-4 border-white opacity-0 transition-opacity duration-300 group-hover:scale-100 group-hover:opacity-100',
          {
            'scale-100 opacity-100': isSelected,
          }
        )}
      />
      <div
        className={clsx(
          'absolute -right-1 -top-1 h-5 w-5 scale-0 rounded-tr-[20px] border-r-4 border-t-4 border-white opacity-0 transition-opacity duration-300 group-hover:scale-100 group-hover:opacity-100',
          {
            'scale-100 opacity-100': isSelected,
          }
        )}
      />
      <div
        className={clsx(
          'absolute -bottom-1 -left-1 h-5 w-5 scale-0 rounded-bl-[20px] border-b-4 border-l-4 border-white opacity-0 transition-opacity duration-300 group-hover:scale-100 group-hover:opacity-100',
          {
            'scale-100 opacity-100': isSelected,
          }
        )}
      />
      <div
        className={clsx(
          'absolute -bottom-1 -right-1 h-5 w-5 scale-0 rounded-br-[20px] border-b-4 border-r-4 border-white opacity-0 transition-opacity duration-300 group-hover:scale-100 group-hover:opacity-100',
          {
            'scale-100 opacity-100': isSelected,
          }
        )}
      />
      <AnimatedLabelDot
        title={loadingIndex === -1 ? 'Loading' : ''}
        focus={loadingIndex === -1}
      />
    </div>
  )
})

const CanvasImage: React.FC<Props> = ({ children, details }) => {
  const {
    canShowCanvas,
    generatedRects,
    canvasReady,
    canvasWidth,
    canvasHeight,
    hideCanvas,
    base64Image,
    selectedIndex,
    hoveredIndex,
    generatedMask,
    generatedImages,
    loadDealsForImage,
    setInitGeneratedMask,
    setHoveredIndex,
    setShouldConvertImage,
    setSelectedIndex,
    setHighlightedCoords,
    clickedRect,
    loadingIndex,
  } = useCanvasContext()

  const analytics = useAnalytics()
  const { openAuthModal } = useAuthContext()
  const { authUser } = useAuthUser()

  // this is to trigger the `handleImageUrlToBase64` effect
  // inside of the CanvasProvider
  useEffect(() => {
    setShouldConvertImage(true)
  }, [setShouldConvertImage])

  return (
    <>
      {children({
        canvasWidth,
        canvasHeight,
      })}

      <div
        className={clsx(
          'canvas-container absolute left-0 top-0 w-full object-cover',
          {
            hidden: !base64Image || hideCanvas || !canShowCanvas,
          }
        )}
      >
        <canvas
          id='canvas'
          width={canvasWidth}
          height={canvasHeight}
          className='border-none'
        />
      </div>

      {/* Semi-transparent overlay */}
      <div
        className={clsx('absolute inset-0 transform-gpu', {
          'bg-black/20 dark:bg-black/30':
            typeof selectedIndex === 'number' || !!generatedMask,
          'bg-black/15 dark:bg-black/25':
            typeof selectedIndex !== 'number' && !!generatedMask,
          'cursor-crosshair': !!authUser,
        })}
        style={{
          WebkitMaskImage: `url(${generatedMask})`,
          maskImage: `url(${generatedMask})`,
          willChange: 'transform, mask-image',
          backfaceVisibility: 'hidden',
          transition: 'background-color 0.2s ease',
        }}
        onClick={(e) => {
          if (!authUser) {
            logger.debug('[CanvasImage] User not authenticated')
            return
          }

          const rect = (e.target as HTMLElement).getBoundingClientRect()
          const x = e.clientX - rect.left
          const y = e.clientY - rect.top

          const width = canvasHeight / 3
          const height = canvasHeight / 3

          // Calculate the top left corner of the square based on the coords
          const left = x - width / 2
          const top = y - height / 2

          setSelectedIndex(undefined)
          setHighlightedCoords({
            top,
            left,
            height,
            width,
          })
        }}
        onMouseEnter={() => setInitGeneratedMask(true)}
        onContextMenu={(e) => e.preventDefault()}
      />

      {/* Display clicked rect */}
      {canvasReady && (
        <>
          {generatedRects.map((rect, index) => {
            return (
              <GeneratedRect
                key={index}
                rect={rect}
                index={index}
                loadingIndex={loadingIndex}
                selectedIndex={selectedIndex}
                hoveredIndex={hoveredIndex}
                setHoveredIndex={setHoveredIndex}
                onClick={() => {
                  analytics.track(AnalyticsEventName.CanvasImageClick, {
                    index,
                    shopKey: details.shopKey,
                    hash: details.hash,
                    source: details.source,
                  })

                  if (!authUser?.uid) {
                    openAuthModal({
                      source: 'canvas-image',
                    })
                    return
                  }

                  const imgSrc = generatedImages[index]?.src
                  if (imgSrc) {
                    loadDealsForImage(imgSrc, index)
                  }
                }}
              />
            )
          })}

          {clickedRect && (
            <ClickedRect
              rect={clickedRect}
              hoveredIndex={hoveredIndex}
              selectedIndex={selectedIndex}
              loadingIndex={loadingIndex}
            />
          )}
        </>
      )}

      {canShowCanvas && !canvasReady && base64Image && (
        <div className='absolute inset-0'>
          <LoadingCanvasDots height={canvasHeight} width={canvasWidth} />
        </div>
      )}
    </>
  )
}

export default CanvasImage
